import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addPolicyFTException,
  deletePolicyFTException,
  editPolicyFTException,
  getPolicyFTExceptionById,
  getPolicyFTExceptions,
} from '../../../../api/endpoints/corporate';
import type { CorporatePolicyFTException } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  fromName: z.string().optional(),
  fromCode: z.string().optional(),
  toName: z.string().optional(),
  toCode: z.string().optional(),
  roundTrip: z.boolean(),
  maxBudget: z.coerce.number().min(0),
  businessClass: z.boolean(),
  firstClass: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  fromName: '',
  fromCode: '',
  toName: '',
  toCode: '',
  roundTrip: false,
  maxBudget: 0,
  businessClass: false,
  firstClass: false,
};

function toFormValues(row: CorporatePolicyFTException): FormValues {
  return {
    fromName: row.fromName ?? '',
    fromCode: row.fromCode ?? '',
    toName: row.toName ?? '',
    toCode: row.toCode ?? '',
    roundTrip: row.roundTrip ?? false,
    maxBudget: row.maxBudget ?? 0,
    businessClass: row.businessClass ?? false,
    firstClass: row.firstClass ?? false,
  };
}

/** Uçak bileti (rota bazlı) istisnaları — Policy/*PolicyFTException routes. */
export function FTExceptionsPanel({ policyID }: { policyID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePolicyFTException | null>(null);
  const [deleting, setDeleting] = useState<CorporatePolicyFTException | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryKey = ['corporate', 'policyFTExceptions', policyID];

  const query = useQuery({ queryKey, queryFn: () => getPolicyFTExceptions(policyID) });
  const rows: CorporatePolicyFTException[] = query.data?.corporateFlightExceptionsFTB ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePolicyFTException(id),
    onSuccess: () => {
      enqueueSnackbar('İstisna silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Uçak Bileti İstisnaları (Rota Bazlı)</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni İstisna
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kalkış</TableCell>
              <TableCell>Varış</TableCell>
              <TableCell>Gidiş-Dönüş</TableCell>
              <TableCell>Maks. Bütçe</TableCell>
              <TableCell>Business</TableCell>
              <TableCell>First</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı istisna bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.flightException3ID} hover>
                <TableCell>{[row.fromName, row.fromCode].filter(Boolean).join(' / ') || '—'}</TableCell>
                <TableCell>{[row.toName, row.toCode].filter(Boolean).join(' / ') || '—'}</TableCell>
                <TableCell>{row.roundTrip ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{row.maxBudget}</TableCell>
                <TableCell>{row.businessClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{row.firstClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <FTExceptionFormDialog
        open={createOpen}
        policyID={policyID}
        row={null}
        onClose={() => setCreateOpen(false)}
        queryKey={queryKey}
      />
      <FTExceptionFormDialog
        open={!!editing}
        policyID={policyID}
        row={editing}
        onClose={() => setEditing(null)}
        queryKey={queryKey}
      />

      <ConfirmDialog
        open={!!deleting}
        title="İstisnayı sil"
        description="Bu uçak bileti istisnasını silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.flightException3ID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}

function FTExceptionFormDialog({
  open,
  policyID,
  row,
  onClose,
  queryKey,
}: {
  open: boolean;
  policyID: number;
  row: CorporatePolicyFTException | null;
  onClose: () => void;
  queryKey: (string | number)[];
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!row;

  const { control, handleSubmit, register, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: row,
    queryKey: ['corporate', 'policyFTException', row?.flightException3ID],
    fetch: async (r) => (await getPolicyFTExceptionById(r.flightException3ID)).corporateFlightExceptionsFTB?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && row
        ? editPolicyFTException({ ...values, policyID, flightException3ID: row.flightException3ID })
        : addPolicyFTException({ ...values, policyID }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'İstisna güncellendi.' : 'İstisna oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'İstisnayı Güncelle' : 'Yeni Uçak Bileti İstisnası'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kalkış Yeri" {...register('fromName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kalkış Kodu" {...register('fromCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Varış Yeri" {...register('toName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Varış Kodu" {...register('toCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('maxBudget')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="roundTrip"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Gidiş-Dönüş"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="businessClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Business Class"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="firstClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="First Class"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
